import type { NostrEvent } from '@nostrify/nostrify';
import {
  getNoteDocument,
  getNoteImage,
  getNoteVideo,
  isDocumentUrl,
  findHashtags,
  isTextRecord,
  stripHashtags,
} from '@/lib/noteUtils';
import { FACETS, TERMS, type Facet, type TermDef } from '@/lib/taxonomy';

export type { Facet } from '@/lib/taxonomy';

/** A term as assigned to a particular note. */
export interface DerivedTag {
  slug: string;
  label: string;
  facet: Facet;
}

/** Length of the "title area" of a note, in characters. */
const HEAD_LENGTH = 220;
/** Minimum score for a term to be assigned (see taxonomy.ts for the rubric). */
const THRESHOLD = 3;

interface CompiledTerm {
  def: TermDef;
  match: RegExp[];
  head: RegExp[];
  url: RegExp[];
}

const globalize = (r: RegExp) =>
  new RegExp(r.source, r.flags.includes('g') ? r.flags : `${r.flags}g`);

/** Terms whose score is computed from text (format terms are attachment-based). */
const COMPILED: CompiledTerm[] = TERMS.filter((t) => t.facet !== 'format').map((def) => ({
  def,
  match: (def.match ?? []).map(globalize),
  head: def.head ?? [],
  url: def.url ?? [],
}));

const TERM_BY_SLUG = new Map(TERMS.map((t) => [t.slug, t]));
const FACET_ORDER = new Map(FACETS.map((f, i) => [f.id, i]));

/** Older slugs (from previously shared links) -> current slug. */
const ALIAS_TO_SLUG = new Map<string, string>();
for (const t of TERMS) for (const a of t.aliases ?? []) ALIAS_TO_SLUG.set(a, t.slug);

export function termBySlug(slug: string): TermDef | undefined {
  return TERM_BY_SLUG.get(slug);
}

/** Resolve a slug from a URL: current slugs pass through, legacy ones map on. */
export function resolveSlug(slug: string): string | null {
  if (TERM_BY_SLUG.has(slug)) return slug;
  return ALIAS_TO_SLUG.get(slug) ?? null;
}

interface Prepared {
  head: string;
  text: string;
  urls: string;
  hashtags: Set<string>;
}

function prepare(event: NostrEvent): Prepared {
  const raw = event.content ?? '';
  const hashtags = new Set<string>();
  for (const t of event.tags) if (t[0] === 't' && t[1]) hashtags.add(t[1].toLowerCase());
  // Links are taken out first, so a "#section" anchor inside a URL is never
  // mistaken for a hashtag.
  const withoutLinks = raw.replace(/nostr:[a-z0-9]+/gi, ' ').replace(/https?:\/\/\S+/gi, ' ');
  for (const h of findHashtags(withoutLinks)) hashtags.add(h);

  const urls = (raw.match(/https?:\/\/\S+/gi) ?? []).join(' ');
  // Scan prose only: links, nostr: references and hashtags are handled on
  // their own, so they can't masquerade as topical mentions.
  const text = stripHashtags(withoutLinks).replace(/\s+/g, ' ').trim();

  return { head: text.slice(0, HEAD_LENGTH), text, urls, hashtags };
}

function score(term: CompiledTerm, p: Prepared): number {
  const { def } = term;
  let s = 0;

  if (def.hashtags?.some((h) => p.hashtags.has(h))) s += 4;
  if (term.url.some((r) => r.test(p.urls))) s += 3;
  if (term.head.some((r) => r.test(p.head))) s += 3;

  if (term.match.length) {
    let hits = 0;
    let inHead = false;
    for (const r of term.match) {
      hits += p.text.match(r)?.length ?? 0;
      if (!inHead) {
        r.lastIndex = 0;
        inHead = r.test(p.head);
        r.lastIndex = 0;
      }
    }
    s += Math.min(hits, 3) + (inHead ? 3 : 0);
  }
  return s;
}

const cache = new Map<string, DerivedTag[]>();

/**
 * Classify a note against the controlled vocabulary. Returns the assigned
 * terms ordered by facet, then by strength of evidence. Results are cached.
 */
export function deriveNoteTags(event: NostrEvent): DerivedTag[] {
  const key = `${event.id}:${event.content?.length ?? 0}:${event.tags.length}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const p = prepare(event);
  const scored: Array<DerivedTag & { score: number }> = [];

  for (const term of COMPILED) {
    const s = score(term, p);
    if (s >= THRESHOLD) {
      scored.push({ slug: term.def.slug, label: term.def.label, facet: term.def.facet, score: s });
    }
  }

  // Format comes from the attachments themselves — reliable, not inferred.
  // A document hosted on a media server must not be mistaken for a picture, so
  // an "image" that is really a PDF or text file doesn't count as one.
  const image = getNoteImage(event);
  if (getNoteVideo(event)) scored.push({ slug: 'video', label: 'Video', facet: 'format', score: 9 });
  else if (image && !isDocumentUrl(image)) scored.push({ slug: 'image', label: 'Image', facet: 'format', score: 9 });
  if (getNoteDocument(event)) scored.push({ slug: 'pdf', label: 'Doc', facet: 'format', score: 9 });
  // Text: the writing itself is the record (a long piece, or a note that is
  // only words). A caption under a picture does not make a note "text".
  if (isTextRecord(event)) scored.push({ slug: 'text', label: 'Text', facet: 'format', score: 9 });

  scored.sort(
    (a, b) =>
      (FACET_ORDER.get(a.facet) ?? 99) - (FACET_ORDER.get(b.facet) ?? 99) || b.score - a.score,
  );

  const result = scored.map(({ slug, label, facet }) => ({ slug, label, facet }));
  cache.set(key, result);
  return result;
}

/** Just the slugs for a note. */
export function deriveNoteTagSlugs(event: NostrEvent): string[] {
  return deriveNoteTags(event).map((t) => t.slug);
}

/** The assigned terms belonging to one facet. */
export function tagsInFacet(tags: DerivedTag[], facet: Facet): DerivedTag[] {
  return tags.filter((t) => t.facet === facet);
}
